import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  BlockStack,
  InlineStack,
  Button,
  Text,
  ChoiceList,
  TextField,
  Banner,
  Box,
  Tag,
} from "@shopify/polaris";
import { ResourcePicker } from "@shopify/app-bridge/actions";
import { AppBridgeContext } from "../AppBridgeProvider";
import CountryPickerModal from "./CountryPickerModal";
import ExcludePagesModal from "./ExcludePagesModal";
import ExcludedPagesSummary from "./ExcludedPagesSummary";
import SelectedCountryList from "./SelectedCountryList";
import { ResourcePickerBlock } from "./ResourcePickerList";
import { normalizePlacementPages } from "../../utils/placementDefaults";

function numericId(gid) {
  if (!gid) return null;
  const m = String(gid).match(/(\d+)$/);
  return m ? m[1] : gid;
}

function mapProductSelection(selection) {
  return (selection || [])
    .map((s) => {
      const firstImage = (s.images && s.images[0]) || {};
      return {
        id: numericId(s.id),
        title: s.title || `Product #${numericId(s.id)}`,
        image: firstImage.originalSrc || firstImage.src || s.image || null,
      };
    })
    .filter((p) => p.id);
}

function mapCollectionSelection(selection) {
  return (selection || [])
    .map((s) => ({
      id: numericId(s.id),
      title: s.title || `Collection #${numericId(s.id)}`,
      image: s.image?.originalSrc || s.image?.src || s.image || null,
    }))
    .filter((c) => c.id);
}

function mergeById(existing, incoming) {
  const map = new Map();
  (existing || []).forEach((item) => {
    if (item?.id != null) map.set(String(item.id), item);
  });
  (incoming || []).forEach((item) => {
    if (item?.id == null) return;
    const prev = map.get(String(item.id));
    map.set(String(item.id), prev ? { ...prev, ...item } : item);
  });
  return Array.from(map.values());
}

function normalizeProducts(pl) {
  if (Array.isArray(pl.selected_products) && pl.selected_products.length) {
    return pl.selected_products;
  }
  return (pl.product_ids || []).map((id) => ({ id, title: `Product #${id}` }));
}

function normalizeCollections(pl) {
  if (Array.isArray(pl.selected_collections) && pl.selected_collections.length) {
    return pl.selected_collections;
  }
  return (pl.collection_ids || []).map((id) => ({ id, title: `Collection #${id}` }));
}

function normalizeExcludeProducts(exclude) {
  if (Array.isArray(exclude?.selected_products) && exclude.selected_products.length) {
    return exclude.selected_products;
  }
  return (exclude?.product_ids || []).map((id) => ({ id, title: `Product #${id}` }));
}

const PLACEMENT_CHILD_BOX_PROPS = {
  paddingBlockStart: "200",
  className: "fsb-placement-choice-children",
};

function normalizeSiteWidePages(pages) {
  if (pages === "site_wide" || pages === "all_products") return "every_page";
  return pages || "every_page";
}

function normalizeExcludedPages(pl) {
  const ex = pl.excluded_pages || {};
  return {
    page_types: ex.page_types || [],
    specific_urls: ex.specific_urls || [],
    url_keywords: ex.url_keywords || [],
  };
}

function TagInputList({ tags, onChange, placeholder, helpText }) {
  const [draft, setDraft] = useState("");
  const fieldWrapRef = useRef(null);

  const addTag = useCallback(
    (raw) => {
      const next = String(raw ?? draft).trim();
      if (!next) return;
      const exists = (tags || []).some((t) => String(t).toLowerCase() === next.toLowerCase());
      if (!exists) onChange([...(tags || []), next]);
      setDraft("");
    },
    [draft, tags, onChange],
  );

  const removeTag = useCallback(
    (tag) => onChange((tags || []).filter((t) => t !== tag)),
    [tags, onChange],
  );

  // Polaris TextField does not forward onKeyDown — attach Enter handler on the native input.
  useEffect(() => {
    const root = fieldWrapRef.current;
    if (!root) return undefined;
    const input = root.querySelector("input");
    if (!input) return undefined;

    const onKeyDown = (e) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      e.stopPropagation();
      addTag(input.value);
    };

    input.addEventListener("keydown", onKeyDown);
    return () => input.removeEventListener("keydown", onKeyDown);
  }, [addTag]);

  return (
    <BlockStack gap="200">
      <div ref={fieldWrapRef} className="fsb-placement-field-wrap">
        <TextField
          label="Add tags"
          labelHidden
          placeholder={placeholder || "Type tag and press Enter"}
          value={draft}
          onChange={setDraft}
          autoComplete="off"
          helpText={helpText}
          connectedRight={
            <Button onClick={() => addTag(draft)} disabled={!String(draft || "").trim()}>
              Add
            </Button>
          }
        />
      </div>
      {(tags || []).length ? (
        <InlineStack gap="200" wrap>
          {(tags || []).map((tag) => (
            <Tag key={tag} onRemove={() => removeTag(tag)}>
              {tag}
            </Tag>
          ))}
        </InlineStack>
      ) : null}
    </BlockStack>
  );
}

export default function PlacementTargetingSection({ placement, placementType = "product_page", onPatch }) {
  const app = useContext(AppBridgeContext);
  const [countryModalOpen, setCountryModalOpen] = useState(false);
  const [excludePagesModalOpen, setExcludePagesModalOpen] = useState(false);
  const pl = placement || {};
  const isCartPlacement = placementType === "cart_page";
  const isSiteWide = placementType === "announcement_bar";
  const pages = isSiteWide
    ? normalizeSiteWidePages(pl.pages)
    : normalizePlacementPages(pl.pages, placementType);
  const geo = pl.geolocation || { mode: "all_world", countries: [] };
  const exclude = pl.exclude || { mode: "none", product_ids: [], tags: [] };

  const selectedProducts = useMemo(() => normalizeProducts(pl), [pl]);
  const selectedCollections = useMemo(() => normalizeCollections(pl), [pl]);
  const excludedPages = useMemo(() => normalizeExcludedPages(pl), [pl]);
  const excludeProducts = useMemo(() => normalizeExcludeProducts(exclude), [exclude]);

  const patchProducts = useCallback(
    (products) => {
      onPatch({
        selected_products: products,
        product_ids: products.map((p) => p.id),
      });
    },
    [onPatch],
  );

  const patchCollections = useCallback(
    (collections) => {
      onPatch({
        selected_collections: collections,
        collection_ids: collections.map((c) => c.id),
      });
    },
    [onPatch],
  );

  const patchExcludeProducts = useCallback(
    (products) => {
      onPatch({
        exclude: {
          ...exclude,
          selected_products: products,
          product_ids: products.map((p) => p.id),
        },
      });
    },
    [exclude, onPatch],
  );

  const patchExcludedPages = useCallback(
    (next) => {
      onPatch({
        excluded_pages: {
          page_types: next.page_types || [],
          specific_urls: next.specific_urls || [],
          url_keywords: next.url_keywords || [],
        },
      });
    },
    [onPatch],
  );

  const pickProducts = useCallback(
    (field) => {
      if (!app) return;
      try {
        const existing = field === "exclude" ? excludeProducts : selectedProducts;
        const initialSelectionIds = existing.map((p) => ({
          id: String(p.id).startsWith("gid://") ? p.id : `gid://shopify/Product/${p.id}`,
        }));
        const picker = ResourcePicker.create(app, {
          resourceType: ResourcePicker.ResourceType.Product,
          options: { selectMultiple: true, showVariants: false, initialSelectionIds },
        });
        picker.subscribe(ResourcePicker.Action.SELECT, (payload) => {
          const mapped = mapProductSelection(payload.selection || []);
          const merged = mergeById(existing, mapped);
          if (field === "exclude") {
            patchExcludeProducts(merged);
          } else {
            patchProducts(merged);
          }
          picker.unsubscribe();
        });
        picker.subscribe(ResourcePicker.Action.CANCEL, () => picker.unsubscribe());
        picker.dispatch(ResourcePicker.Action.OPEN);
      } catch (e) {
        console.error("[Placement] product picker", e);
      }
    },
    [app, excludeProducts, patchExcludeProducts, patchProducts, selectedProducts],
  );

  const pickCollections = useCallback(() => {
    if (!app) return;
    try {
      const initialSelectionIds = selectedCollections.map((c) => ({
        id: String(c.id).startsWith("gid://") ? c.id : `gid://shopify/Collection/${c.id}`,
      }));
      const picker = ResourcePicker.create(app, {
        resourceType: ResourcePicker.ResourceType.Collection,
        options: { selectMultiple: true, initialSelectionIds },
      });
      picker.subscribe(ResourcePicker.Action.SELECT, (payload) => {
        const mapped = mapCollectionSelection(payload.selection || []);
        patchCollections(mergeById(selectedCollections, mapped));
        picker.unsubscribe();
      });
      picker.subscribe(ResourcePicker.Action.CANCEL, () => picker.unsubscribe());
      picker.dispatch(ResourcePicker.Action.OPEN);
    } catch (e) {
      console.error("[Placement] collection picker", e);
    }
  }, [app, patchCollections, selectedCollections]);

  const removeProduct = useCallback(
    (id) => patchProducts(selectedProducts.filter((p) => String(p.id) !== String(id))),
    [patchProducts, selectedProducts],
  );

  const removeCollection = useCallback(
    (id) => patchCollections(selectedCollections.filter((c) => String(c.id) !== String(id))),
    [patchCollections, selectedCollections],
  );

  const removeExcludeProduct = useCallback(
    (id) => patchExcludeProducts(excludeProducts.filter((p) => String(p.id) !== String(id))),
    [excludeProducts, patchExcludeProducts],
  );

  const patchCountries = useCallback(
    (countries) => {
      onPatch({
        geolocation: {
          ...geo,
          mode: geo.mode || "specific_countries",
          countries,
        },
      });
    },
    [geo, onPatch],
  );

  const removeCountry = useCallback(
    (code) => patchCountries((geo.countries || []).filter((c) => c !== code)),
    [geo.countries, patchCountries],
  );

  const siteWidePageChoices = useMemo(
    () => [
      {
        label: "Show on every page",
        value: "every_page",
        renderChildren: (isSelected) =>
          isSelected ? (
            <Box {...PLACEMENT_CHILD_BOX_PROPS}>
              <div className="fsb-placement-resource-panel">
                <Button onClick={() => setExcludePagesModalOpen(true)}>Select pages to exclude</Button>
                <ExcludedPagesSummary excludedPages={excludedPages} />
              </div>
            </Box>
          ) : null,
      },
      { label: "Show on home page only", value: "home_only" },
      { label: "Show on all product pages", value: "all_product_pages" },
      {
        label: "Show on specific product pages",
        value: "specific_products",
        renderChildren: (isSelected) =>
          isSelected ? (
            <Box {...PLACEMENT_CHILD_BOX_PROPS}>
              <BlockStack gap="200">
                {!app ? <Banner tone="warning">Open from Shopify admin to pick products.</Banner> : null}
                <ResourcePickerBlock
                  buttonLabel="Select products"
                  onPick={() => pickProducts("pages")}
                  items={selectedProducts}
                  onRemove={removeProduct}
                  emptyLabel="No products selected."
                />
              </BlockStack>
            </Box>
          ) : null,
      },
      {
        label: "Show on all products in specific collections",
        value: "specific_collections",
        renderChildren: (isSelected) =>
          isSelected ? (
            <Box {...PLACEMENT_CHILD_BOX_PROPS}>
              <BlockStack gap="200">
                {!app ? <Banner tone="warning">Open from Shopify admin to pick collections.</Banner> : null}
                <ResourcePickerBlock
                  buttonLabel="Select collections"
                  onPick={pickCollections}
                  items={selectedCollections}
                  onRemove={removeCollection}
                  emptyLabel="No collections selected."
                />
              </BlockStack>
            </Box>
          ) : null,
      },
      { label: "Show on all collection pages", value: "all_collection_pages" },
      {
        label: "Show on specific collection pages",
        value: "specific_collection_pages",
        renderChildren: (isSelected) =>
          isSelected ? (
            <Box {...PLACEMENT_CHILD_BOX_PROPS}>
              <BlockStack gap="200">
                {!app ? <Banner tone="warning">Open from Shopify admin to pick collections.</Banner> : null}
                <ResourcePickerBlock
                  buttonLabel="Select collections"
                  onPick={pickCollections}
                  items={selectedCollections}
                  onRemove={removeCollection}
                  emptyLabel="No collections selected."
                />
              </BlockStack>
            </Box>
          ) : null,
      },
    ],
    [excludedPages, pickCollections, pickProducts, removeCollection, removeProduct, selectedCollections, selectedProducts],
  );

  const pageChoices = useMemo(
    () => [
      { label: "All products", value: "all_products" },
      {
        label: "Specific products",
        value: "specific_products",
        renderChildren: (isSelected) =>
          isSelected ? (
            <Box {...PLACEMENT_CHILD_BOX_PROPS}>
              <BlockStack gap="200">
                {!app ? <Banner tone="warning">Open from Shopify admin to pick products.</Banner> : null}
                <ResourcePickerBlock
                  buttonLabel="Select products"
                  onPick={() => pickProducts("pages")}
                  items={selectedProducts}
                  onRemove={removeProduct}
                  emptyLabel="No products selected."
                />
              </BlockStack>
            </Box>
          ) : null,
      },
      {
        label: "All products in specific collections",
        value: "specific_collections",
        renderChildren: (isSelected) =>
          isSelected ? (
            <Box {...PLACEMENT_CHILD_BOX_PROPS}>
              <BlockStack gap="200">
                {!app ? <Banner tone="warning">Open from Shopify admin to pick collections.</Banner> : null}
                <ResourcePickerBlock
                  buttonLabel="Select collections"
                  onPick={pickCollections}
                  items={selectedCollections}
                  onRemove={removeCollection}
                  emptyLabel="No collections selected."
                />
              </BlockStack>
            </Box>
          ) : null,
      },
      {
        label: "All products with specific tags",
        value: "specific_tags",
        renderChildren: (isSelected) =>
          isSelected ? (
            <Box {...PLACEMENT_CHILD_BOX_PROPS}>
              <TagInputList
                tags={pl.tags || []}
                onChange={(next) => onPatch({ tags: next })}
                placeholder="Type tag and press Enter"
                helpText="Press Enter to add each tag"
              />
            </Box>
          ) : null,
      },
    ],
    [
      app,
      onPatch,
      pickCollections,
      pickProducts,
      pl.tags,
      removeCollection,
      removeProduct,
      selectedCollections,
      selectedProducts,
    ],
  );

  const geoChoices = useMemo(
    () => [
      {
        label: "All world",
        value: "all_world",
        helpText: isCartPlacement || isSiteWide ? "Excluding specific countries from other boosters" : undefined,
      },
      {
        label: "Specific countries",
        value: "specific_countries",
        renderChildren: (isSelected) =>
          isSelected ? (
            <Box {...PLACEMENT_CHILD_BOX_PROPS}>
              <div className="fsb-placement-resource-panel">
                <Button onClick={() => setCountryModalOpen(true)}>Select countries</Button>
                <SelectedCountryList
                  codes={geo.countries || []}
                  onRemove={removeCountry}
                  emptyLabel="No countries selected."
                />
              </div>
            </Box>
          ) : null,
      },
    ],
    [geo.countries, isCartPlacement, isSiteWide, removeCountry],
  );

  const excludeChoices = useMemo(
    () => [
      { label: "Don't exclude products", value: "none" },
      {
        label: "Exclude specific products",
        value: "specific_products",
        renderChildren: (isSelected) =>
          isSelected ? (
            <Box {...PLACEMENT_CHILD_BOX_PROPS}>
              <BlockStack gap="200">
                {!app ? <Banner tone="warning">Open from Shopify admin to pick products.</Banner> : null}
                <ResourcePickerBlock
                  buttonLabel="Select products to exclude"
                  onPick={() => pickProducts("exclude")}
                  items={excludeProducts}
                  onRemove={removeExcludeProduct}
                  emptyLabel="No excluded products selected."
                />
              </BlockStack>
            </Box>
          ) : null,
      },
      {
        label: "Exclude products with specific tags",
        value: "specific_tags",
        renderChildren: (isSelected) =>
          isSelected ? (
            <Box {...PLACEMENT_CHILD_BOX_PROPS}>
              <TagInputList
                tags={exclude.tags || []}
                onChange={(next) => onPatch({ exclude: { ...exclude, tags: next } })}
                placeholder="Type tag and press Enter"
                helpText="Press Enter to add each tag"
              />
            </Box>
          ) : null,
      },
    ],
    [app, exclude.tags, excludeProducts, onPatch, pickProducts, removeExcludeProduct],
  );

  return (
    <div className="fsb-placement-targeting">
      <BlockStack gap="500">
        {isSiteWide ? (
          <ChoiceList
            title="Select pages to display the bar"
            choices={siteWidePageChoices}
            selected={[pages]}
            onChange={(v) => onPatch({ pages: v[0] || "every_page" })}
          />
        ) : !isCartPlacement ? (
          <ChoiceList
            title="Select pages to display the bar"
            choices={pageChoices}
            selected={[pages]}
            onChange={(v) => onPatch({ pages: v[0] || "all_products" })}
          />
        ) : null}

        <ChoiceList
          title="Geolocation targeting"
          choices={geoChoices}
          selected={[geo.mode || "all_world"]}
          onChange={(v) => onPatch({ geolocation: { ...geo, mode: v[0] || "all_world" } })}
        />

        <BlockStack gap="200">
          {isCartPlacement || isSiteWide ? (
            <Text as="p" variant="bodySm" tone="subdued">
              Exclude products that don&apos;t require shipping, like digital products or gift cards.
            </Text>
          ) : null}
          <ChoiceList
            title="Exclude products"
            choices={excludeChoices}
            selected={[exclude.mode || "none"]}
            onChange={(v) => onPatch({ exclude: { ...exclude, mode: v[0] || "none" } })}
          />
        </BlockStack>

        <CountryPickerModal
          open={countryModalOpen}
          onClose={() => setCountryModalOpen(false)}
          selected={geo.countries || []}
          onSelect={patchCountries}
        />

        <ExcludePagesModal
          open={excludePagesModalOpen}
          onClose={() => setExcludePagesModalOpen(false)}
          excludedPages={excludedPages}
          onSelect={patchExcludedPages}
        />
      </BlockStack>
    </div>
  );
}
